import { Controller, useForm, useWatch } from 'react-hook-form'
import { Dropdown, Select, Switch } from 'antd'
import { ReactComponent as IconLark } from '../../../../images/setting-noti/lark.svg'
import { ReactComponent as IconLine } from '../../../../images/setting-noti/line.svg'
import { ReactComponent as IconGroup } from '../../../../images/setting-noti/Group.svg'
import { ReactComponent as IconWarning } from '../../../../images/warning2.svg'
import LabelCustom from '../../../../components/custom/LabelCustom'
import { Button, Card, Col, Form, Row } from 'react-bootstrap'
import { useEffect, useRef, useState } from 'react'
import { v4 as uuidv4 } from 'uuid'

import { HiOutlinePlus } from 'react-icons/hi'
import { TbEdit } from 'react-icons/tb'
import AlertSuccess from '../../../../alert/AlertSuccess'
import Error_false from '../../../../components/error/error-false'
import {
  delNotiAPI,
  getCheckUrlLarkGAPI,
  getDetailAttendanceNotiAPI,
  putNotiGroupDetailsAPI,
} from '../../../../services/setting/attendance-noti'
import useSearchLineG from '../../../../hooks/use-search-lineG'
import ModalAlert from '../../../../components/modal-alert'
import { useTranslation } from 'react-i18next'

export default function SocialGroupsForm({ hasId, attendanceId }) {
  /* ================= FORM ================= */
  const {
    control,
    reset,
    getValues,
    setError,
    clearErrors,
    formState: { errors }, // 👈 เพิ่มตรงนี้
  } = useForm({
    defaultValues: { groups: {} },
  })
  const debounceRef = useRef()
  const { t } = useTranslation()
  /* ================= STATE ================= */
  const [groups, setGroups] = useState([])
  const [editingGroupId, setEditingGroupId] = useState(null)
  const [isDelete, setIsDelete] = useState(false)
  const [openDelete, setOpenDelete] = useState(false)
  const [deleteId, setDeleteId] = useState(null)
  const [refreshLineKey, setRefreshLineKey] = useState(0)
  const optiomLineG = useSearchLineG(attendanceId, refreshLineKey)
  const watchedGroup = useWatch({
    control,
    name: editingGroupId ? `groups.${editingGroupId}` : undefined,
  })

  /* ================= CONFIG ================= */
  const SOCIAL_GROUPS = [
    {
      key: 'lark',
      channelTypeKey: 'hrms_notification_add_group_lark',
      labelGroup: 'hrms_notification_lark_group_header',
      icon: <IconLark />,
      fields: [
        {
          name: 'webhookUrl',
          label: 'hrms_notification_lark_webhook',
          required: true,
          component: 'textarea',
          placeholderKey: 'hrms_notification_place_holder_lark_webhook',
        },
        {
          name: 'signature',
          label: 'hrms_notification_lark_signature',
          component: 'input',
          placeholderKey: 'hrms_notification_place_holder_lark_signature',
        },
        {
          name: 'status',
          label: 'hrms_notification_title_status',
          component: 'switch',
        },
      ],
    },
    {
      key: 'line',
      channelTypeKey: 'hrms_notification_add_group_line',
      labelGroup: 'hrms_notification_line_group_header',
      icon: <IconLine />,
      fields: [
        {
          name: 'groupName',
          label: 'hrms_notification_line_group_name',
          required: true,
          component: 'select',
          placeholderKey: 'hrms_notification_place_holder_line',
        },
        {
          name: 'status',
          label: 'hrms_notification_title_status',
          component: 'switch',
        },
      ],
    },
  ]

  const ICON_MAP = {
    lark: <IconLark />,
    line: <IconLine />,
  }

  /* ================= FETCH GROUPS ================= */
  const fetchGroups = async () => {
    const res = await getDetailAttendanceNotiAPI({ attendanceId })
    if (!res?.status) return

    const notifyChannels = res.result?.notifyChannels ?? []
    const mappedGroups = []
    const formGroups = {}

    notifyChannels.forEach((channel) => {
      const groupKey = channel.channelType === 'LARK' ? 'lark' : 'line'
      const config = SOCIAL_GROUPS.find((g) => g.key === groupKey)
      if (!config) return

      const id = channel.thirdPartyId

      // ===== UI card =====
      mappedGroups.push({
        id,
        key: groupKey,
        thirdPartyId: id,
        labelGroup: config.labelGroup,
        fields: config.fields,
        originalWebhookUrl: channel.larkWebhookUrl ?? '',
      })

      // ===== preload form =====
      if (groupKey === 'lark') {
        formGroups[id] = {
          webhookUrl: channel.larkWebhookUrl ?? '',
          signature: channel.larkSignatureVerification ?? '',
          status: channel.isActive === 1,
        }
      }

      if (groupKey === 'line') {
        formGroups[id] = {
          groupName: channel.notifyLineGroupId
            ? {
                value: channel.notifyLineGroupId,
                label: channel.lineGroupName,
              }
            : null,
          status: channel.isActive === 1,
        }
      }
    })

    setGroups(mappedGroups)
    reset({ groups: formGroups })
  }

  useEffect(() => {
    if (attendanceId) fetchGroups()
  }, [attendanceId])

  /* ================= HANDLERS ================= */
  const handleAdd = (key) => {
    if (editingGroupId) return // กันแก้หลาย card พร้อมกัน
    const config = SOCIAL_GROUPS.find((g) => g.key === key)
    const id = uuidv4()
    setGroups((prev) => [
      {
        id, // uuid ใหม่ทุกครั้ง
        key, // lark / line
        thirdPartyId: null, // new group
        labelGroup: config.labelGroup,
        fields: config.fields,
      },
      ...prev,
    ])

    setEditingGroupId(id)
  }

  const handleRemove = async (groupId) => {
    if (!groupId) return

    const group = groups.find((g) => g.id === groupId)
    if (!group) return

    // 🔹 กลุ่มใหม่ (ยังไม่ save) → ลบ UI ทันที
    if (!group.thirdPartyId) {
      setGroups((prev) => prev.filter((g) => g.id !== groupId))
      setEditingGroupId(null)
      return
    }

    try {
      // 🔹 กลุ่มจาก backend → ลบจริง
      const resp = await delNotiAPI({
        attendanceNotifyType: 'SOCIAL_GROUP',
        id: group.thirdPartyId,
      })

      // ❌ API fail → UI ไม่หาย
      if (!resp?.status) {
        Error_false(resp)
        return
      }

      // ✅ success → ค่อยลบ UI
      setGroups((prev) => prev.filter((g) => g.id !== groupId))

      const values = structuredClone(getValues())
      if (values.groups) {
        delete values.groups[groupId]
      }
      reset(values)
      setEditingGroupId(null)

      AlertSuccess('Delete successfully.')

      // ⭐ ถ้าเป็น LINE → reload dropdown
      if (group.key === 'line') {
        setRefreshLineKey((prev) => prev + 1)
      }
    } catch (error) {
      console.error(error)
    }
  }

  const handleSave = async () => {
    const values = getValues().groups?.[editingGroupId]
    const group = groups.find((g) => g.id === editingGroupId)
    if (!group || !values) return

    let body = {}

    if (group.key === 'lark') {
      body = {
        thirdPartyId: group.thirdPartyId,
        attendanceId,
        channelType: 'LARK',
        larkWebhookUrl: values.webhookUrl,
        larkSignatureVerification: values.signature,
        isActive: values.status ? 1 : 0,
      }
    }

    if (group.key === 'line') {
      body = {
        thirdPartyId: group.thirdPartyId,
        attendanceId,
        channelType: 'LINE',
        notifyLineGroupId: values.groupName?.value,
        isActive: values.status ? 1 : 0,
      }
    }

    const resp = await putNotiGroupDetailsAPI(body)
    if (!resp?.status) {
      Error_false(resp)
      return
    }
    AlertSuccess()
    setEditingGroupId(null)
    // ⭐ reload → group ที่ save จะขึ้นบนสุด

    // ⭐ ถ้าเป็น LINE → reload dropdown
    if (group.key === 'line') {
      setRefreshLineKey((prev) => prev + 1)
    }
    fetchGroups()
  }
  const checkLarkWebhook = async (groupId, webhookUrl) => {
    if (!webhookUrl) return

    const group = groups.find((g) => g.id === groupId)
    const oldWebhookUrl = group?.originalWebhookUrl ?? ''

    if (webhookUrl === oldWebhookUrl) {
      clearErrors(`groups.${groupId}.webhookUrl`)
      return
    }

    try {
      const res = await getCheckUrlLarkGAPI({
        attendanceId,
        larkWebhookUrl: webhookUrl,
        checkType: 'LARK',
      })

      // ❌ validation fail จาก backend
      if (res?.status === false) {
        setError(`groups.${groupId}.webhookUrl`, {
          type: 'manual',
          message:
            res?.error?.[0]?.msg || // เช่น "Invalid value"
            res?.msg ||
            'Invalid value',
        })
        return
      }

      // ❌ duplicate
      if (res?.result?.isDuplicate === true) {
        setError(`groups.${groupId}.webhookUrl`, {
          type: 'manual',
          message: t('hrms_notification_error_validate_webhook'),
        })
        return
      }

      // ✅ ผ่าน
      clearErrors(`groups.${groupId}.webhookUrl`)
    } catch (e) {
      setError(`groups.${groupId}.webhookUrl`, {
        type: 'manual',
        message: t('Cannot verify webhook URL'),
      })
    }
  }

  const webhookError =
    editingGroupId && errors?.groups?.[editingGroupId]?.webhookUrl

  const hasFormError = !!webhookError

  const handleCancelGroup = (groupId) => {
    if (!hasId) {
      // ❌ ยังไม่ save → ลบทั้ง card
      setGroups((prev) => prev.filter((g) => g.id !== groupId))
      setEditingGroupId(null)
      return
    }

    // ✅ มี id แล้ว → reset + reload
    setEditingGroupId(null)
    fetchGroups()
  }

  /* ================= RENDER ================= */
  return (
    <div>
      <Col xl={12} className="d-flex justify-content-between mb-3">
        <div
          style={{
            color: 'var(--color-on-base-content-high)',
            fontWeight: 500,
            fontSize: '18px',
          }}
        >
          {t('cms_notification_social_groups_header')}
        </div>

        {hasId && !editingGroupId && (
          <Dropdown
            menu={{
              className: 'add-group-dropdown',
              style: {
                border: '1px solid #d9d9d9',
                borderRadius: 8,
              },
              items: SOCIAL_GROUPS.map((g) => ({
                key: g.key,
                label: (
                  <div className="d-flex align-items-center gap-2">
                    {g.icon}
                    {t(g.labelGroup)}
                  </div>
                ),
              })),
              onClick: ({ key }) => handleAdd(key),
            }}
          >
            <Button className="bt-primary" style={{ whiteSpace: 'nowrap' }}>
              <HiOutlinePlus className="me-2" />
              {t('hrms_notification_btn_add_group')}
            </Button>
          </Dropdown>
        )}
      </Col>

      {!hasId ? (
        <div className="text-center py-5">
          <IconGroup />
          <div style={{ color: 'var(--color-on-base-content-low)' }}>
            {t('hrms_notification_place_holder_create_a_group_to_get_start')}
          </div>
        </div>
      ) : (
        groups.map((group, index) => {
          const isEdit = editingGroupId === group.id
          const currentValues = getValues()
          const editingGroup = groups.find((g) => g.id === editingGroupId)
          const isLarkWebhookEmpty =
            editingGroup?.key === 'lark' && !watchedGroup?.webhookUrl
          const isLineGroupEmpty =
            editingGroup?.key === 'line' && !watchedGroup?.groupName?.value

          const isButtonDisabled = hasFormError
            ? true
            : isLarkWebhookEmpty || isLineGroupEmpty

          return (
            <Card key={group.id} className="mb-4">
              <Card.Body>
                <Col className="d-flex justify-content-between my-auto">
                  <div
                    style={{ height: '40px' }}
                    className="d-flex align-items-center"
                  >
                    <div
                      style={{
                        color: 'var(--color-on-base-content-high)',
                        fontWeight: 500,
                        fontSize: '16px',
                      }}
                    >
                      {index + 1}. {ICON_MAP[group.key]} {t(group.labelGroup)}
                    </div>
                  </div>

                  {!isEdit && (
                    <Button
                      style={{ whiteSpace: 'nowrap' }}
                      className="bt-primary"
                      size="sm"
                      onClick={() => setEditingGroupId(group.id)}
                    >
                      <TbEdit className="me-2" />
                      {t('hrms_notification_btn_edit')}
                    </Button>
                  )}
                </Col>
                <Row className="gy-3">
                  {group.fields.map((field) => (
                    <Col
                      md={field.component === 'switch' ? 12 : 6}
                      key={field.name}
                    >
                      <Controller
                        name={`groups.${group.id}.${field.name}`}
                        control={control}
                        render={({ field: f }) => {
                          if (field.component === 'switch') {
                            const isActive = !!f.value

                            /* ===== READONLY MODE ===== */
                            if (!isEdit) {
                              return (
                                <div className="d-flex align-items-center ">
                                  {/* LABEL */}
                                  <LabelCustom className="fw-medium mb-0 me-2">
                                    {t(field.label)}:
                                  </LabelCustom>

                                  {/* BADGE */}
                                  <span
                                    style={{
                                      padding: '6px 18px',
                                      borderRadius: 6,
                                      fontWeight: 500,
                                      height: '32px',
                                      fontSize: 12,
                                      backgroundColor: isActive
                                        ? 'var(--color-functional-foreground-success-subtle)'
                                        : 'var(--color-functional-foreground-error-subtle)',
                                      color: isActive
                                        ? 'var(--color-functional-content-success)'
                                        : 'var(--color-functional-content-error)',
                                      whiteSpace: 'nowrap',
                                    }}
                                  >
                                    {isActive
                                      ? t('hrms_notification_status_active')
                                      : t('hrms_notification_status_inactive')}
                                  </span>
                                </div>
                              )
                            }

                            /* ===== EDIT MODE ===== */
                            return (
                              <>
                                <LabelCustom className="mb-1 fw-medium">
                                  {t(field.label)}
                                </LabelCustom>

                                <div className="d-flex align-items-center gap-3 mt-1">
                                  <Switch
                                    checked={isActive}
                                    onChange={f.onChange}
                                    className="switch-active-green"
                                  />
                                  <span
                                    style={{
                                      fontSize: '14px',
                                      fontWeight: 400,
                                      color:
                                        ' var(--color-on-base-content-subtle)',
                                    }}
                                  >
                                    {isActive
                                      ? t('hrms_notification_status_active')
                                      : t('hrms_notification_status_inactive')}
                                  </span>
                                </div>
                              </>
                            )
                          }

                          /* ================= OTHER FIELDS ================= */
                          return (
                            <>
                              <LabelCustom className="mb-1 fw-medium">
                                {t(field.label)}
                              </LabelCustom>

                              {field.component === 'select' ? (
                                <Select
                                  className="custom-placeholder"
                                  {...f}
                                  labelInValue
                                  style={{ width: '100%', height: '40px' }}
                                  disabled={!isEdit}
                                  showSearch
                                  optionFilterProp="label" // ⭐ สำคัญ
                                  filterOption={(input, option) =>
                                    option?.label
                                      ?.toLowerCase()
                                      .includes(input.toLowerCase())
                                  }
                                  value={f.value ?? undefined}
                                  onChange={(val) => f.onChange(val)}
                                  options={optiomLineG}
                                  placeholder={t(field.placeholderKey)}
                                />
                              ) : (
                                <Controller
                                  name={`groups.${group.id}.${field.name}`}
                                  control={control}
                                  render={({ field: f, fieldState }) => (
                                    <>
                                      <Form.Control
                                        className="custom-input"
                                        {...f}
                                        placeholder={t(field.placeholderKey)}
                                        as={
                                          field.component === 'textarea'
                                            ? 'textarea'
                                            : 'input'
                                        }
                                        disabled={!isEdit}
                                        isInvalid={
                                          field.component === 'textarea' &&
                                          !!fieldState.error
                                        }
                                        {...(field.component === 'textarea' && {
                                          rows: 3,
                                          onChange: (e) => {
                                            f.onChange(e)

                                            clearTimeout(debounceRef.current)
                                            debounceRef.current = setTimeout(
                                              () => {
                                                checkLarkWebhook(
                                                  group.id,
                                                  e.target.value,
                                                )
                                              },
                                              200,
                                            )
                                          },
                                        })}
                                      />

                                      {/* {field.component === 'textarea' &&
                                        fieldState.error && (
                                          <div className="invalid-feedback d-block">
                                            {t(
                                              'hrms_notification_error_validate_webhook',
                                            )}
                                          </div>
                                        )} */}
                                      {field.component === 'textarea' &&
                                        fieldState.error && (
                                          <div className="invalid-feedback d-block">
                                            {fieldState.error.message}
                                          </div>
                                        )}
                                    </>
                                  )}
                                />
                              )}
                            </>
                          )
                        }}
                      />
                    </Col>
                  ))}
                </Row>

                {isEdit && (
                  <Row className="mt-2">
                    {group.thirdPartyId ? (
                      <Col
                        xl={6}
                        md={6}
                        onClick={() => {
                          // 🔹 group ใหม่ → ลบทันที
                          if (!group.thirdPartyId) {
                            handleRemove(group.id)
                            return
                          }
                          // 🔹 group จาก backend → confirm ก่อน
                          setDeleteId(group.id)
                          setOpenDelete(true)
                        }}
                      >
                        <Button className="px-4 btn-responsive bt-del">
                          {t('hrms_notification_btn_delete')}
                        </Button>
                      </Col>
                    ) : (
                      <Col></Col>
                    )}

                    <Col
                      xl={6}
                      md={6}
                      className={`flex-md-row justify-content-end flex-md-nowrap d-md-flex gap-2 ${
                        !hasId ? 'd-flex' : ''
                      }`}
                    >
                      {/* ปุ่มซ้าย */}
                      <Button
                        type="button"
                        className={`btn-responsive bt-cancel ${!hasId ? '' : 'mt-2'}`}
                        onClick={() => handleCancelGroup(group.id)}
                      >
                        {t('hrms_notification_btn_cancel')}
                        {/* {group.thirdPartyId
                          ? t('cms_notification_btn_reset')
                          : t('hrms_notification_btn_cancel')} */}
                      </Button>

                      {/* ปุ่มขวา */}
                      <Button
                        disabled={isButtonDisabled}
                        type="button"
                        className={`btn-responsive ${!hasId ? '' : 'mt-2'}`}
                        style={{
                          borderRadius: 12,
                          whiteSpace: 'nowrap',
                          borderColor: isButtonDisabled
                            ? 'var(--color-on-base-border-disable)'
                            : '',
                          backgroundColor: isButtonDisabled
                            ? 'var(--comp-btn-color-background-disable)'
                            : '',
                          color: isButtonDisabled
                            ? 'var(--color-on-base-content-disable)'
                            : '',
                        }}
                        onClick={handleSave}
                      >
                        {t('hrms_notification_btn_save')}
                      </Button>
                    </Col>
                  </Row>
                )}
              </Card.Body>
            </Card>
          )
        })
      )}
      <ModalAlert
        open={openDelete}
        icon={<IconWarning width={74} />}
        title={t('hrms_notification_modal_delete_information_header')}
        description={t('hrms_notification_modal_delete_information_description')}
        okText={t('hrms_notification_btn_yes')}
        cancelText={t('hrms_notification_btn_no')}
        okType="danger"
        okStyle={{
          background: 'var(--color-functional-foreground-error)',
          fontWeight: 500,
          color: 'var(--color-on-primary-content-high)',
        }}
        titleColor="var(--color-accent-content-red)"
        onOk={() => {
          deleteId && handleRemove(deleteId) // ✅ id จริง
          setOpenDelete(false)
          setDeleteId(null)
        }}
        onCancel={() => {
          setOpenDelete(false)
          setDeleteId(null)
        }}
      />
    </div>
  )
}
